// components/carousel/SectionMobileCarousel.js - UPDATED to handle both normal and carousel type feeds
import React from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import useFeedData from '../../hooks/useFeedData';
import useCommitteeFeedData from '../../hooks/useCommitteeFeedData';
import FeedItem from '../feed/FeedItem';
import { generateFeedUrlFromApiEndpoint } from '../../utils/feedUrlUtils';
import { isCommitteeFeed, generateCommitteeFeedUrl, parseCommitteeParams } from '../../utils/committeeFeedHelpers';

const SectionMobileCarousel = ({ 
  feeds, 
  sections, 
  sectionTitle, 
  title,
  filterByHomePage = false  // NEW: Only filter by showOnHomePage when true
}) => {
  const navigate = useNavigate();

  // Support both 'feeds' and 'sections' props for compatibility
  const feedsData = feeds || sections;
  const displayTitle = sectionTitle || title;

  if (!feedsData || !Array.isArray(feedsData)) {
    return (
      <div style={{ padding: '20px', color: 'white', textAlign: 'center' }}>
        <p>No feeds available</p>
      </div>
    );
  }

  const handleFeedClick = (feed) => {
    const apiParams = feed.apiParams || feed.apiEndpoint;
    
    // Check if this is a committee feed
    if (isCommitteeFeed(feed)) {
      const url = generateCommitteeFeedUrl(apiParams);
      window.scrollTo(0, 0);
      navigate(url);
      return;
    }
    
    // Department → agency route override
    if (feed.departmentPage && feed.agencyRoute) {
      window.scrollTo(0, 0);
      navigate(feed.agencyRoute);
      return;
    }

    // If feed has a departmentLink (from carousel type feeds), use that
    if (feed.departmentLink) {
      window.scrollTo(0, 0);
      navigate(feed.departmentLink);
      return;
    }

    // Use enhanced URL generation for regular feeds
    const urlData = generateFeedUrlFromApiEndpoint(apiParams);

    if (urlData) {
      window.scrollTo(0, 0);
      navigate(urlData.fullUrl);
    } else {
      // Fallback parsing
      let cleanParams = apiParams || '';
      if (cleanParams.startsWith('&') || cleanParams.startsWith('?')) {
        cleanParams = cleanParams.substring(1);
      }

      const urlParams = new URLSearchParams(cleanParams);
      const schema = urlParams.get('schema') || 'united_states_of_america';
      const table = urlParams.get('table') || urlParams.get('table_name') || 'default';

      window.scrollTo(0, 0);
      navigate(`/feed/${schema}/${table}`);
    }
  };

  // Create 4 cards with incremented skip values for a normal feed
  const createCardsFromNormalFeed = (feed) => {
    const cards = [];
    const isCommittee = isCommitteeFeed(feed);

    for (let i = 0; i < 4; i++) {
      const cardId = `${feed.id}-card-${i}`;

      const cardFeed = {
        ...feed,
        id: cardId,
        skip: i,
        isCommitteeFeed: isCommittee,
      };

      let apiEndpoint = feed.apiParams || feed.apiEndpoint || '';

      if (apiEndpoint.includes('skip=')) {
        apiEndpoint = apiEndpoint.replace(/skip=\d+/, `skip=${i}`);
      } else {
        apiEndpoint += `&skip=${i}`;
      }

      cardFeed.apiParams = apiEndpoint;
      cards.push(cardFeed);
    }

    return cards;
  };

  /**
   * Transform feeds to handle both 'normal' type and 'carousel' type feeds
   * - 'normal' type: use as-is
   * - 'carousel' type: extract carouselItems and convert to normal feed format
   */
  const transformFeeds = (rawFeeds) => {
    const transformedFeeds = [];

    rawFeeds.forEach((feed) => {
      if (!feed) return;

      const feedType = feed.displayType || feed.type;

      if (feedType === 'normal') {
        // Normal feeds - use as-is
        transformedFeeds.push(feed);
      } else if (feedType === 'carousel' && feed.carouselItems && Array.isArray(feed.carouselItems)) {
        // Carousel feeds - extract carouselItems that have apiEndpoint
        feed.carouselItems.forEach((item) => {
          // Skip viewall type items and member type items
          if (item.type === 'viewall' || item.type === 'member') return;
          
          // Skip items without apiEndpoint
          if (!item.apiEndpoint && !item.apiParams) return;

          // Convert carousel item to normal feed format
          const normalizedFeed = {
            id: item.id,
            type: 'normal',
            displayType: 'normal',
            name: item.title || feed.departmentTitle,
            title: item.title || feed.departmentTitle,
            apiEndpoint: item.apiEndpoint,
            apiParams: item.apiEndpoint, // Normalize to apiParams
            feedName: item.feedName || item.title,
            feedBranch: item.branch || feed.branch || 'Executive',
            feedTopic: item.topic || feed.topic || 'N/A',
            buttonName: item.buttonName || 'Document',
            viewAllLink: feed.departmentLink || item.viewAllLink,
            departmentLink: feed.departmentLink, // Preserve for navigation
            showOnHomePage: item.showOnHomePage !== false,
            useCommitteeAPI: item.useCommitteeAPI || false,
          };

          transformedFeeds.push(normalizedFeed);
        });
      }
    });

    return transformedFeeds;
  };

  // Transform and filter feeds
  const allTransformedFeeds = transformFeeds(feedsData);
  
  // FIXED: Only filter by showOnHomePage when filterByHomePage prop is true
  // This allows section pages to show ALL feeds, not just featured ones
  const normalFeeds = allTransformedFeeds.filter((feed) => {
    if (!feed) return false;
    // Only apply showOnHomePage filter when explicitly requested
    if (filterByHomePage) {
      return feed.showOnHomePage !== false;
    }
    // For section pages, show all feeds
    return true;
  });

  if (normalFeeds.length === 0) {
    return (
      <div style={{ padding: '20px', color: 'white', textAlign: 'center' }}>
        <p>No feeds available</p>
        <p style={{ fontSize: '12px', opacity: 0.7 }}>
          Total feeds: {feedsData.length}, Section: {displayTitle}
        </p>
      </div>
    );
  }

  return (
    <>
      {normalFeeds.map((feed) => {
        if (!feed || !feed.id) return null;

        const cardsForFeed = createCardsFromNormalFeed(feed);
        const feedName = feed.name || feed.title;
        const isCommittee = isCommitteeFeed(feed);

        return (
          <div
            key={feed.id}
            className="homepage-branch-carousel"
            style={{ marginBottom: '2rem' }}
          >
            {/* Heading */}
            {feedName && (
              <div
                id="pageLink"
                className="carouselGroupTitleLink"
                style={{ cursor: 'pointer' }}
                onClick={() => handleFeedClick(feed)}
              >
                <h5 className="carousel-heading">
                  <div className="title-container">
                    <span className="heading-text">{feedName}</span>
                    <ChevronRight id="clickChevron" className="heading-chevron" />
                  </div>
                </h5>
              </div>
            )}

            {/* Carousel */}
            <div className="carouselGroupContainer news-section">
              <div className="scroll-container">
                <div className="gradient-left" />
                <div className="gradient-right" />
                <div className="scroll-area">
                  <div className="card-container">
                    {cardsForFeed.map((cardFeed) => (
                      isCommittee ? (
                        <CommitteeMobileCard key={cardFeed.id} feed={cardFeed} />
                      ) : (
                        <SectionMobileCard key={cardFeed.id} feed={cardFeed} />
                      )
                    ))}

                    {/* View All button */}
                    <div className="card-wrapper view-all-card">
                      <div id="viewFullPageButton">
                        <Button
                          id="viewFullLegislativePageButtonMobile"
                          size="lg"
                          onClick={() => handleFeedClick(feed)}
                        >
                          {feed.departmentPage && feed.agencyRoute
                            ? `View ${feedName} page`
                            : `View all ${feedName}`}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Spacer */}
            <div className="carousel-spacer">
              <div id="carouSpacer" />
            </div>
          </div>
        );
      })}

      {/* Complete carousel CSS */}
      <style>{`
        .homepage-branch-carousel {
          margin-bottom: 2rem;
        }

        .carouselGroupTitleLink {
          text-decoration: none !important;
          color: inherit !important;
        }

        .carousel-heading {
          position: relative;
          padding: 16px 30px 8px 20px;
          margin: 0;
          color: white;
          height: 50px;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .title-container {
          display: inline-flex;
          align-items: center;
          max-width: 100%;
          min-width: 0;
        }

        .heading-text {
          font-family: var(--site-font);
          font-size: clamp(15px, 4vw, 18px);
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: calc(100% - 24px);
          margin-right: 4px;
        }

        .heading-chevron {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          min-width: 16px;
          display: inline-block;
        }

        .carouselGroupContainer {
          height: 285px;
        }

        .news-section {
          margin-bottom: 2rem;
        }

        .scroll-container {
          position: relative;
          height: 290px;
        }

        .gradient-left, .gradient-right {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 48px;
          z-index: 5;
          pointer-events: none;
        }

        .gradient-left {
          left: 0;
        }

        .gradient-right {
          right: 0;
        }

        .scroll-area {
          box-sizing: border-box;
          height: 290px;
          overflow-x: scroll;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 17px;
        }

        .scroll-area::-webkit-scrollbar {
          display: none;
        }

        .card-container {
          display: flex;
          padding-left: 10px;
          padding-right: 10px;
          height: 280px;
        }

        .card-content {
          height: 160px;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          overflow: hidden;
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
        }

        .card-wrapper {
          flex: 0 0 430px !important;
          padding-right: 1rem;
          scroll-snap-align: center;
          box-sizing: border-box;
          background: transparent;
        }

        .card-wrapper:first-child {
          padding-left: 1rem;
        }

        .card-content {
          height: 160px;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          overflow: hidden;
          background: transparent; 
        }

        .card-loading-container {
          height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .view-all-card {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        #viewFullPageButton {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        #viewFullLegislativePageButtonMobile {
          border: none;
          color: white;
          width: calc(100%);
          text-align: center;
          position: relative;
        }

        .carousel-spacer {
          margin-bottom: 1rem;
        }

        @media screen and (min-width: 768px) {
          .card-wrapper {
            flex: 0 0 320px;
          }
        }

        @media screen and (max-width: 350px) {
          .heading-text {
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

// Regular feed card component
const SectionMobileCard = ({ feed }) => {
  const apiParams = feed.apiParams || feed.apiEndpoint || '';
  const { data, loading, error } = useFeedData(apiParams, 1);

  if (!feed) {
    return (
      <div className="card-wrapper">
        <div>
          <p style={{ color: 'white', fontSize: '14px', textAlign: 'center' }}>
            Invalid feed data
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card-wrapper">
        <div className="card-loading-container">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="card-wrapper">
        <div className="card-content" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.05)'
        }}>
          <p style={{ color: 'white', fontSize: '14px', textAlign: 'center' }}>
            {error ? 'Error loading content' : 'No content available'}
          </p>
        </div>
      </div>
    );
  }

  const urlParams = new URLSearchParams((apiParams || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || 'united_states_of_america';
  const table = urlParams.get('table') || urlParams.get('table_name') || 'default';

  const feedName = feed.name || feed.title;
  const feedBranch = feed.feedBranch || feed.branch;
  const feedTopic = feed.feedTopic || feed.topic;
  const feedButtonName = feed.buttonName;

  return (
    <div className="card-wrapper">
      <div className="card-content" style={{ height: '160px', overflow: 'hidden' }}>
        <FeedItem
          item={data[0]}
          feedName={feedName}
          branch={feedBranch}
          topic={feedTopic}
          buttonName={feedButtonName}
          schema={schema}
          table={table}
          expanded={false}
          variant="homepage"
        />
      </div>
    </div>
  );
};

// Committee feed card component using useCommitteeFeedData
const CommitteeMobileCard = ({ feed }) => {
  const apiParams = feed.apiParams || feed.apiEndpoint || '';
  const { data, loading, error } = useCommitteeFeedData(apiParams, 1);

  if (!feed) {
    return (
      <div className="card-wrapper">
        <div>
          <p style={{ color: 'white', fontSize: '14px', textAlign: 'center' }}>
            Invalid feed data
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card-wrapper">
        <div className="card-loading-container">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="card-wrapper">
        <div className="card-content" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.05)'
        }}>
          <p style={{ color: 'white', fontSize: '14px', textAlign: 'center' }}>
            {error ? 'Error loading content' : 'No content available'}
          </p>
        </div>
      </div>
    );
  }

  const feedName = feed.name || feed.title;
  const feedBranch = feed.feedBranch || feed.branch;
  const feedTopic = feed.feedTopic || feed.topic;
  const feedButtonName = feed.buttonName;

  return (
    <div className="card-wrapper">
      <div className="card-content" style={{ height: '160px', overflow: 'hidden' }}>
        <FeedItem
          item={data[0]}
          feedName={feedName}
          branch={feedBranch}
          topic={feedTopic}
          buttonName={feedButtonName}
          schema="united_states_of_america"
          table="committee_materials"
          expanded={false}
          variant="homepage"
        />
      </div>
    </div>
  );
};

export default SectionMobileCarousel;